import clsx from 'clsx'
import { useEffect } from 'react'
import { styled, Box, keyframes } from '@mui/system'
import { useLocation } from 'react-router-dom'
import { Icon, ButtonBase } from '@mui/material'
import React, { useState, useRef, useCallback } from 'react'

// Keyframe animations
const shimmer = keyframes`
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
`

const pulseGlow = keyframes`
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.05); }
`

const borderFlow = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`

const NavExpandRoot = styled('div')(({ theme }) => ({
    position: 'relative',
    marginBottom: '2px',
    
    '& .expandIcon': {
        transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        transform: 'rotate(90deg)',
    },
    '& .collapseIcon': {
        transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        transform: 'rotate(0deg)',
    },
    '& .expansion-panel': {
        overflow: 'hidden',
        transition: 'max-height 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.35s ease, transform 0.4s ease',
        marginLeft: '16px',
        paddingLeft: '16px',
        position: 'relative',
        opacity: 1,
        transform: 'translateY(0)',
        
        // Animated gradient border
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '8px',
            bottom: '8px',
            width: '2px',
            borderRadius: '1px',
            background: `linear-gradient(180deg, 
                ${theme.palette.primary.main}, 
                ${theme.palette.primary.light}, 
                ${theme.palette.secondary?.main || theme.palette.primary.dark},
                ${theme.palette.primary.main}
            )`,
            backgroundSize: '100% 200%',
            animation: `${borderFlow} 3s ease infinite`,
            opacity: 0.7,
        },
        
        '&[style*="max-height: 0px"]': {
            opacity: 0,
            transform: 'translateY(-8px)',
        },
    },
    '& .highlight': {
        background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}10)`,
    },
    '&.compactNavItem': {
        width: 44,
        overflow: 'hidden',
        justifyContent: 'center !important',
        '& .itemText': {
            display: 'none',
        },
        '& .itemIcon': {
            display: 'none',
        },
    },
}))

const BaseButton = styled(ButtonBase)(({ theme }) => ({
    height: 52,
    width: '100%',
    whiteSpace: 'pre',
    overflow: 'hidden',
    paddingRight: '16px',
    paddingLeft: '6px',
    borderRadius: '14px',
    marginBottom: '4px',
    display: 'flex',
    justifyContent: 'space-between !important',
    color: theme.palette.text.primary,
    position: 'relative',
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    isolation: 'isolate',
    fontFamily: '"Poppins", sans-serif',
    
    // Glassmorphism background
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '14px',
        background: 'transparent',
        backdropFilter: 'blur(0px)',
        transition: 'all 0.3s ease',
        zIndex: -1,
    },
    
    // Gradient border on hover
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '14px',
        padding: '1px',
        background: 'transparent',
        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'xor',
        WebkitMaskComposite: 'xor',
        transition: 'all 0.3s ease',
        opacity: 0,
        zIndex: -1,
    },
    
    '&:hover': {
        transform: 'translateX(6px) scale(1.01)',
        
        '&::before': {
            background: `linear-gradient(135deg, 
                rgba(255, 255, 255, 0.08) 0%, 
                rgba(255, 255, 255, 0.02) 50%,
                rgba(255, 255, 255, 0.05) 100%
            )`,
            backdropFilter: 'blur(8px)',
        },
        
        '&::after': {
            opacity: 1,
            background: `linear-gradient(135deg, 
                ${theme.palette.primary.main}60,
                ${theme.palette.primary.light}40,
                transparent,
                ${theme.palette.primary.main}30
            )`,
        },
        
        '& .icon-wrapper': {
            transform: 'scale(1.08)',
            boxShadow: `0 4px 20px ${theme.palette.primary.main}40`,
        },
        
        '& .glow-effect': {
            opacity: 1,
        },
    },
    
    '&.open': {
        '&::before': {
            background: `linear-gradient(135deg, 
                ${theme.palette.primary.main}15 0%, 
                ${theme.palette.primary.main}05 50%,
                ${theme.palette.primary.main}10 100%
            )`,
            backdropFilter: 'blur(12px)',
        },
        
        '&::after': {
            opacity: 0.6,
            background: `linear-gradient(135deg, 
                ${theme.palette.primary.main}80,
                ${theme.palette.primary.light}50,
                ${theme.palette.primary.main}40
            )`,
        },
        
        '& .icon-wrapper': {
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            boxShadow: `0 4px 16px ${theme.palette.primary.main}50`,
            
            '& .MuiIcon-root': {
                color: '#fff',
            },
        },
    },
    
    // Icon wrapper styles
    '& .icon-wrapper': {
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '12px',
        marginRight: '6px',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        background: `linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))`,
        position: 'relative',
        overflow: 'hidden',
        
        '& .MuiIcon-root': {
            fontSize: '20px',
            transition: 'all 0.3s ease',
            position: 'relative',
            zIndex: 1,
        },
        
        // Inner glow
        '&::before': {
            content: '""',
            position: 'absolute',
            inset: '2px',
            borderRadius: '10px',
            background: 'transparent',
            transition: 'all 0.3s ease',
        },
    },
    
    // Glow effect behind icon
    '& .glow-effect': {
        position: 'absolute',
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        background: `radial-gradient(circle, ${theme.palette.primary.main}40, transparent 70%)`,
        left: '4px',
        top: '50%',
        transform: 'translateY(-50%)',
        opacity: 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: 'none',
        filter: 'blur(8px)',
    },
    
    // Chevron wrapper styling
    '& .chevron-wrapper': {
        width: 30,
        height: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '10px',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        background: 'rgba(255, 255, 255, 0.04)',
        position: 'relative',
        overflow: 'hidden',
        
        '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '10px',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}30, transparent)`,
            opacity: 0,
            transition: 'opacity 0.3s ease',
        },
    },
    
    '&:hover .chevron-wrapper': {
        background: 'rgba(255, 255, 255, 0.08)',
        transform: 'scale(1.05)',
        
        '&::before': {
            opacity: 1,
        },
    },
    
    '&.open .chevron-wrapper': {
        background: `${theme.palette.primary.main}30`,
        boxShadow: `0 2px 8px ${theme.palette.primary.main}20`,
    },
}))

const BulletIcon = styled('div')(({ theme }) => ({
    width: 10,
    height: 10,
    color: 'inherit',
    overflow: 'hidden',
    marginLeft: '16px',
    marginRight: '12px',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    boxShadow: `0 0 12px ${theme.palette.primary.main}60, inset 0 0 4px rgba(255,255,255,0.3)`,
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    position: 'relative',
    
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: '2px',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.3)',
        opacity: 0.5,
    },
    
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: '-3px',
        borderRadius: '50%',
        background: `${theme.palette.primary.main}`,
        opacity: 0,
        animation: `${pulseGlow} 2s ease-in-out infinite`,
        zIndex: -1,
        filter: 'blur(4px)',
    },
}))

const ItemText = styled('span')(({ theme }) => ({
    fontSize: '0.9rem',
    fontWeight: 500,
    paddingLeft: '0.6rem',
    verticalAlign: 'middle',
    letterSpacing: '0.02em',
    transition: 'all 0.3s ease',
    background: 'linear-gradient(90deg, currentColor, currentColor)',
    backgroundSize: '200% 100%',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    position: 'relative',
    fontFamily: '"Poppins", sans-serif',
    
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-2px',
        left: '0.6rem',
        right: 0,
        height: '1px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`,
        transform: 'scaleX(0)',
        transformOrigin: 'left',
        transition: 'transform 0.3s ease',
    },
}))

const BadgeValue = styled('div')(({ theme }) => ({
    padding: '3px 10px',
    overflow: 'hidden',
    borderRadius: '20px',
    fontSize: '0.7rem',
    fontWeight: 700,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: theme.palette.primary.contrastText,
    minWidth: '22px',
    textAlign: 'center',
    boxShadow: `0 3px 12px ${theme.palette.primary.main}50, inset 0 1px 0 rgba(255,255,255,0.2)`,
    position: 'relative',
    letterSpacing: '0.03em',
    fontFamily: '"Poppins", sans-serif',
    
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
        animation: `${shimmer} 2s ease-in-out infinite`,
    },
}))

const MatxVerticalNavExpansionPanel = ({ item, children, mode }) => {
    const [collapsed, setCollapsed] = useState(true)
    const elementRef = useRef(null)
    const componentHeight = useRef(0)
    const { pathname } = useLocation()
    const { name, icon, iconText, badge } = item

    const handleClick = () => {
        componentHeight.current = 0
        calcaulateHeight(elementRef.current)
        setCollapsed(!collapsed)
    }

    const calcaulateHeight = useCallback((node) => {
        if (node.name !== 'child') {
            for (let child of node.children) {
                calcaulateHeight(child)
            }
        }

        if (node.name === 'child') componentHeight.current += node.scrollHeight
        else componentHeight.current += 52 // Updated to match new height
        return
    }, [])

    useEffect(() => {
        if (!elementRef) return

        calcaulateHeight(elementRef.current)

        for (let child of elementRef.current.children) {
            if (child.getAttribute('href') === pathname) {
                setCollapsed(false)
            }
        }
    }, [pathname, calcaulateHeight])

    return (
        <NavExpandRoot>
            <BaseButton
                className={clsx({
                    'has-submenu compactNavItem': true,
                    compactNavItem: mode === 'compact',
                    open: !collapsed,
                })}
                onClick={handleClick}
            >
                {/* Glow effect */}
                <div className="glow-effect" />
                
                <Box display="flex" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                    {icon && (
                        <div className="icon-wrapper">
                            <Icon sx={{ color: 'inherit' }}>
                                {icon}
                            </Icon>
                        </div>
                    )}
                    {iconText && <BulletIcon />}
                    <ItemText className="sidenavHoverShow">{name}</ItemText>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1} sx={{ position: 'relative', zIndex: 1 }}>
                    {badge && (
                        <BadgeValue className="sidenavHoverShow itemIcon">
                            {badge.value}
                        </BadgeValue>
                    )}
                    <div
                        className={clsx({
                            'sidenavHoverShow chevron-wrapper': true,
                            collapseIcon: collapsed,
                            expandIcon: !collapsed,
                        })}
                    >
                        <Icon 
                            fontSize="small" 
                            sx={{ 
                                verticalAlign: 'middle',
                                fontSize: '1.2rem',
                                opacity: 0.8,
                                transition: 'all 0.3s ease',
                            }}
                        >
                            chevron_right
                        </Icon>
                    </div>
                </Box>
            </BaseButton>

            <div
                ref={elementRef}
                className="expansion-panel submenu"
                style={
                    collapsed
                        ? { maxHeight: '0px' }
                        : { maxHeight: componentHeight.current + 'px' }
                }
            >
                {children}
            </div>
        </NavExpandRoot>
    )
}

export default MatxVerticalNavExpansionPanel
